import getLexicalDependencies from './getWorkflowStepDependencies/getLexicalDependencies.js';

const isBatchPagination = (step) => {
  const paginationOptions = step?.options?.pagination || {};
  const paginationType = paginationOptions?.type || 'default';
  return paginationType === 'batch';
};

export const getWorkflowStepDependencies = (step, opts = {}) => {
  const getStepIdsFromArg = (key, arg = {}, opts) => {
    // Handle nested arrays
    if (Array.isArray(arg)) {
      const deps = [];
      arg.forEach((item) => {
        deps.push(...getStepIdsFromArg(key, item, opts));
      });
      return deps;
    }

    if (arg?.type === 'lexical') {
      return [...getLexicalDependencies(arg?.value, key, opts)];
    }
    if (arg?.type === 'arrayMap') {
      return [arg?.stepId];
    }
    if (arg?.type === 'code') {
      const deps = getCustomStepIds(arg);
      if (opts?.fetchVariables === true && arg?.code?.includes('memory')) {
        deps.push('memory');
      }
      return deps;
    }

    // Handle plain objects by delegating to getStepIdsFromObject
    if (arg && typeof arg === 'object' && arg?.constructor === Object) {
      return getStepIdsFromObject(arg);
    }

    return [];
  };

  const getStepIdsFromObject = (obj = {}) => {
    const set = [];
    for (const key in obj) {
      const arg = obj[key];
      if (Array.isArray(arg)) {
        arg.forEach((n) => {
          set.push(...getStepIdsFromArg(key, n, opts));
        });
      } else {
        set.push(...getStepIdsFromArg(key, arg, opts));
      }
    }
    return set;
  };

  const getStepIdsFromAdvancedFilters = (obj = {}) => {
    const set = [];

    // Process a single filter object
    const processFilter = (filter) => {
      if (!filter) return;

      // Handle nested filter groups
      if (filter.type === 'nested' && filter.filterSet) {
        filter.filterSet.forEach(processFilter);
        return;
      }

      // Extract step IDs from field (can be lexical or simple object)
      if (filter.field) {
        if (filter.field.type === 'lexical') {
          set.push(...getLexicalDependencies(filter.field.value, 'field', opts));
        } else if (filter.field.stepId) {
          set.push(filter.field.stepId);
        }
      }

      // Extract step IDs from value (always lexical)
      if (filter.value && filter.value.type === 'lexical') {
        set.push(...getLexicalDependencies(filter.value.value, 'value', opts));
      }
    };

    // Process the filterSet array
    if (obj.filterSet && Array.isArray(obj.filterSet)) {
      obj.filterSet.forEach(processFilter);
    }

    return set;
  };

  const getCustomStepIds = (obj = {}) => {
    const set = [];
    if (obj?.stepId) {
      set.push(obj?.stepId);
    }
    if (obj?.inputStepIds) {
      if (Array.isArray(obj?.inputStepIds)) {
        set.push(...obj.inputStepIds.filter((id) => typeof id === 'string'));
      } else if (typeof obj?.inputStepIds === 'string') {
        set.push(obj.inputStepIds);
      }
    }
    if (obj?.inputStepIdParents) {
      if (Array.isArray(obj?.inputStepIdParents)) {
        set.push(...obj.inputStepIdParents.filter((id) => typeof id === 'string'));
      } else if (typeof obj?.inputStepIdParents === 'string') {
        set.push(obj.inputStepIdParents);
      } else if (obj.inputStepIdParents && typeof obj.inputStepIdParents === 'object' && obj.inputStepIdParents.constructor === Object) {
        for (const stepId in obj.inputStepIdParents) {
          set.push(stepId);
          const parentIds = obj.inputStepIdParents[stepId];
          if (Array.isArray(parentIds)) {
            set.push(...parentIds.filter((id) => typeof id === 'string'));
          } else if (typeof parentIds === 'string') {
            set.push(parentIds);
          }
        }
      }
    }
    if (opts?.fetchVariables === true && obj?.code?.includes?.('memory')) {
      set.push('memory');
    }
    return set;
  };

  const getFlowControlValueDeps = (flowControlConfig = {}) => {
    const deps = [];
    if (!flowControlConfig) {
      return deps;
    }
    if (flowControlConfig.emailTo?.value) {
      deps.push(flowControlConfig.emailTo.value);
    }
    if (flowControlConfig.emailSubject?.value) {
      deps.push(flowControlConfig.emailSubject.value);
    }
    if (flowControlConfig.emailBody?.value) {
      deps.push(flowControlConfig.emailBody.value);
    }
    if (flowControlConfig.logMessage?.value) {
      deps.push(flowControlConfig.logMessage.value);
    }
    if (flowControlConfig.slackMessageBody?.value) {
      deps.push(flowControlConfig.slackMessageBody.value);
    }
    return deps;
  };

  const allStepIds = [];

  // First we deal with arrays (guard with Array.isArray to prevent TypeError when value is non-array truthy)
  const conditionalAuth = Array.isArray(step?.options?.conditionalAuth) ? step.options.conditionalAuth.map((n) => [n.field?.value, n.value?.value]).flat() : [];
  const filters = Array.isArray(step?.options?.filters) ? step.options.filters.map((n) => n?.value?.value) : [];
  const slices = Array.isArray(step?.options?.slices) ? step.options.slices.map((n) => [n.from?.value, n.to?.value]).flat() : [];
  const memory = Array.isArray(step?.options?.memory) ? step.options.memory.map((n) => n.value?.value) : [];
  const postProcessingFilters = Array.isArray(step?.options?.postProcessing?.filters) ? step.options.postProcessing.filters.map((n) => n?.value?.value) : [];
  const postProcessingSlices = Array.isArray(step?.options?.postProcessing?.slices) ? step.options.postProcessing.slices.map((n) => [n.from?.value, n.to?.value]).flat() : [];
  const retryOnStatusCodes = step?.options?.retries?.retryOnStatusCodes?.type === 'lexical' ? [step.options.retries.retryOnStatusCodes.value] : [];
  const fileName = step?.options?.fileName ? getStepIdsFromObject(step.options.fileName) : [];

  // // Flow control email and slack dependencies
  const flowControlDeps = [];
  const flowControl = step?.options?.flowControl;
  if (flowControl) {
    flowControlDeps.push(...getFlowControlValueDeps(flowControl));
    flowControlDeps.push(...getFlowControlValueDeps(flowControl.onFailure));
    flowControlDeps.push(...getFlowControlValueDeps(flowControl.onSuccess));
    flowControlDeps.push(...getFlowControlValueDeps(flowControl.onNoResults));
  }

  [...conditionalAuth, ...filters, ...slices, ...memory, ...postProcessingFilters, ...postProcessingSlices, ...retryOnStatusCodes, ...flowControlDeps].forEach((n) => {
    allStepIds.push(...getLexicalDependencies(n, 'value', opts));
  });
  allStepIds.push(...fileName);

  // Process advanced filters from all possible locations
  allStepIds.push(...getStepIdsFromAdvancedFilters(step?.options?.advancedFilters));
  allStepIds.push(...getStepIdsFromAdvancedFilters(step?.options?.postProcessing?.advancedFilters));
  allStepIds.push(...getStepIdsFromAdvancedFilters(step?.arguments?.conditions));

  // Next we deal with objects
  allStepIds.push(...getStepIdsFromObject(step?.arguments || {}));
  allStepIds.push(...getStepIdsFromObject(step?.options?.customArguments || {}));
  allStepIds.push(...getStepIdsFromObject(step?.options?.inputArguments || {}));
  allStepIds.push(...getStepIdsFromObject(step?.options?.arguments || {}));
  allStepIds.push(...getCustomStepIds(step?.arguments || {}));

  if (opts?.fetchVariables === true && isBatchPagination(step) && step?.options?.pagination?.rememberPage === true) {
    allStepIds.push('memory');
  }

  if (opts?.parents && Array.isArray(opts?.parents) && opts?.parents?.length > 0) {
    const batchPaginationParents = opts?.parents?.filter((p) => isBatchPagination(p));
    if (batchPaginationParents?.length > 0) {
      batchPaginationParents.forEach((p) => {
        allStepIds.push(...getWorkflowStepDependencies(p));
      });
    }
  }

  if (opts?.fetchVariables === true) {
    return [...new Set(allStepIds?.flat()?.filter((n) => !!n))];
  }

  return [...new Set(allStepIds?.flat()?.filter((n) => !!n && !['system', 'memory'].includes(n)))];
};
